import { getCourses } from '../../../../../../Utils/apiService';
import { fetchAssessmentForms } from '../../../Helper/AssessmentFormsHelper';
import {
    fetchActivities,
    fetchActivitySubmissions,
    createActivity,
    ActivitySubmission,
} from '../../../Helper/ActivitiesHelper';
import { getUserDetails } from '../../../../../../storageService';

export interface FacultyCourse {
    TermName: string;
    CourseName: string;
    Section: string;
    CourseOfferingID: string;
    enrolledNumber: string;
    ProgramPlanId?: string;
}

export interface FacultyActivityItem {
    id: string;
    name: string;
    description: string;
    dueDate?: string;
    courseOfferingId: string;
    batchName: string;
    published: boolean;
    submittedCount: number;
    totalEnrolledCount: number;
    referenceFileName?: string;
    referenceContentVersionId?: string;
    referenceUrl?: string;
    isReferenceLink?: boolean;
}

export const fetchFacultyCourses = async (): Promise<FacultyCourse[]> => {
    const contactId = getUserDetails()?.ContactId;
    if (!contactId) return [];
    const courseData = await getCourses(contactId);
    return courseData || [];
};

// Salesforce Activities-backed (RestApi_Activities.cls) — batchId is the
// course's ProgramPlanId, same convention as CourseArtifacts/
// DiscussionForum/AssessmentForms.
export const fetchFacultyActivities = async (course: FacultyCourse): Promise<FacultyActivityItem[]> => {
    if (!course.ProgramPlanId) return [];
    const items = await fetchActivities(course.ProgramPlanId, 'Faculty');
    return items.map((item) => ({
        id: item.activityId,
        name: item.title,
        description: item.description,
        dueDate: item.dueDate,
        courseOfferingId: course.CourseOfferingID,
        batchName: course.TermName,
        published: item.status === 'Active',
        submittedCount: item.submittedCount,
        totalEnrolledCount: item.totalEnrolledCount,
        referenceFileName: item.referenceFileName,
        referenceContentVersionId: item.referenceContentVersionId,
        referenceUrl: item.referenceUrl,
        isReferenceLink: item.isReferenceLink,
    }));
};

export interface FacultyFormItem {
    id: string;
    name: string;
    formType: string;
    courseOfferingId: string;
    batchName: string;
    published: boolean;
    questionCount: number;
    enrolledCount: number;
    respondedCount: number;
}

// "Pre & Post test" forms are ungraded knowledge-check forms, Salesforce
// AssessmentForms-backed — batchId is the course's ProgramPlanId, not its
// CourseOfferingID (confirmed: "batchId refers to hed__Program_Plan__c,
// never Course Offering Id").
export const fetchFacultyForms = async (course: FacultyCourse): Promise<FacultyFormItem[]> => {
    const contactId = getUserDetails()?.ContactId;
    if (!contactId || !course.ProgramPlanId) return [];
    const forms = await fetchAssessmentForms(course.ProgramPlanId, contactId, 'Faculty');
    return forms.map((item) => ({
        id: item.formId,
        name: item.title,
        formType: item.formType,
        courseOfferingId: course.CourseOfferingID,
        batchName: course.TermName,
        published: item.status === 'Published',
        questionCount: item.questionCount,
        enrolledCount: item.enrolledCount,
        respondedCount: item.respondedCount,
    }));
};

export type FacultySubmissionRow = ActivitySubmission;

export const fetchFacultySubmissions = async (activityId: string): Promise<FacultySubmissionRow[]> =>
    fetchActivitySubmissions(activityId);

export interface NewActivityInput {
    title: string;
    courseOfferingId: string;
    brief: string;
    dueDate: string;
    file?: File;
}

// batchId for createActivity is the course's ProgramPlanId, resolved from
// the selected courseOfferingId — same convention as fetchFacultyActivities.
export const createFacultyActivity = async (input: NewActivityInput, courses: FacultyCourse[]) => {
    const contactId = getUserDetails()?.ContactId;
    const course = courses.find((c) => c.CourseOfferingID === input.courseOfferingId);
    if (!contactId || !course?.ProgramPlanId) {
        throw new Error('Unable to resolve batch for this activity.');
    }
    return createActivity({
        contactId,
        batchId: course.ProgramPlanId,
        title: input.title,
        description: input.brief,
        dueDate: input.dueDate || undefined,
        file: input.file,
    });
};
